import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  BulkOperationQueueConfig,
  BulkOperationGroup,
  BulkOperationItem,
  OperationStatus,
  BulkActivityItem,
} from './BulkOperationQueue.interface';
import { useQueryClient } from '@tanstack/react-query';
import { useBulkActivitiesPolling } from './useBulkActivitiesPolling';
import {
  BULK_OPERATION_QUEUE_API_SERVICES,
  BULK_ACTIVITIES_QUERY_KEYS,
} from './api';
import {
  selectContactImport,
  selectContactImportById,
  selectUser,
} from '@/store/bootstrap/selectors';
import { fetchPendingImport, fetchImportById } from '@/store/bootstrap/actions';

// View mode values
const VIEW_MODE_EXPANDED = 'expanded';
const VIEW_MODE_MINIMIZED = 'minimized';
const VIEW_MODE_HIDDEN = 'hidden';

// Activity status values
const STATUS_IN_PROGRESS = 'in_progress';
const STATUS_COMPLETED = 'completed';
const STATUS_PENDING = 'pending';
const STATUS_FAILED = 'failed';

// UI / display strings (exported for NavActivityTracker)
export const UI_ACTIVITIES_COMPLETED = 'Activities completed';
export const UI_ACTIVITY_TRACKER = 'Activity Tracker';
const UI_PROCESSING = 'Processing...';
const UI_ACTIVITIES_IN_PROGRESS_SUFFIX = ' Activities in-progress';
const UI_IMPORTING_CSV = 'Importing CSV';
const UI_CSV_IMPORT = 'CSV Import';
const UI_PROJECT = 'project';
const UI_FLOW = 'flow';
const UI_CSV = 'CSV';
const UI_CANDIDATE = 'candidate';
const UI_CANDIDATES = 'candidates';

// Label prefixes for getActivityLabel (failed / completed / in-progress)
const LABEL_FAILED_TO_EXPORT = 'Failed to export ';
const LABEL_FAILED_TO_IMPORT = 'Failed to import ';
const LABEL_FAILED_TO_ADD = 'Failed to add ';
const LABEL_FAILED_TO_EMAIL = 'Failed to email ';
const LABEL_FAILED_TO_MOVE = 'Failed to move ';
const LABEL_FAILED_TO_REJECT = 'Failed to reject ';
const LABEL_FAILED_TO_ADD_TAGS = 'Failed to add tags to ';
const LABEL_FAILED_TO_SEND = 'Failed to send to ';
const LABEL_FAILED_TO_REACTIVATE = 'Failed to reactivate ';
const LABEL_FAILED_TO_SCORE = 'Failed to score ';
const LABEL_EXPORTED = 'Exported ';
const LABEL_IMPORTED = 'Imported ';
const LABEL_ADDED = 'Added ';
const LABEL_EMAILED = 'Emailed ';
const LABEL_EMAIL_SCHEDULED = 'Email scheduled for ';
const LABEL_MOVED = 'Moved ';
const LABEL_REJECTED = 'Rejected ';
const LABEL_ADDED_TAGS = 'Added tags to ';
const LABEL_SENT = 'Sent to ';
const LABEL_REACTIVATED = 'Reactivated ';
const LABEL_SCORED = 'Scored ';
const LABEL_EXPORTING = 'Exporting ';
const LABEL_IMPORTING = 'Importing ';
const LABEL_ADDING = 'Adding ';
const LABEL_EMAILING = 'Emailing ';
const LABEL_SCHEDULING_EMAIL = 'Scheduling email for ';
const LABEL_MOVING = 'Moving ';
const LABEL_REJECTING = 'Rejecting ';
const LABEL_ADDING_TAGS = 'Adding tags to ';
const LABEL_SENDING = 'Sending to ';
const LABEL_REACTIVATING = 'Reactivating ';
const LABEL_SCORING = 'Scoring ';
const LABEL_FAILED_PREFIX = 'Failed: ';
const LABEL_SCHEDULING_CALLS = 'Scheduling calls for candidates';
const LABEL_SCHEDULED_CALLS = 'Scheduled calls for candidates';
const LABEL_FAILED_TO_SCHEDULE_CALLS = 'Failed to schedule calls for ';

// Event names
const EVENT_BULK_OPERATIONS_COMPLETE = 'bulkOperationsComplete';
const EVENT_BULK_ACTION_STARTED = 'bulkActionStarted';

// ID prefixes
const OP_ID_PREFIX = 'op_';
const CSV_ID_PREFIX = 'csv_';

// Import status (Redux CSV)
const IMPORT_STATUS_DONE = 'done';
const IMPORT_STATUS_FAILED = 'failed';

// Log messages
const LOG_MARK_ACTIVITIES_VIEWED_FAILED =
  'Failed to mark activities as viewed:';

export type BulkOperationViewMode =
  | typeof VIEW_MODE_EXPANDED
  | typeof VIEW_MODE_MINIMIZED
  | typeof VIEW_MODE_HIDDEN;

// Activity types supported - these are the exact keys from the API
export type BulkActivityType =
  | 'add_tag'
  | 'reject_candidate'
  | 'move_to_stage'
  | 'send_email'
  | 'add_to_job'
  | 'add_to_project'
  | 'add_to_flow'
  | 'send_self_schedule'
  | 'invite_hireflix'
  | 'export_csv'
  | 'ai_scoring'
  | 'import_csv'
  | 'reactivate_application'
  | 'kombo_document_upload'
  | 'ai_coordinator_dispatch'
  | 'background_verification';

// Local operation for client-side tracking
export interface LocalBulkOperation {
  id: string;
  activityType: BulkActivityType;
  title: string;
  status: OperationStatus;
  progress: number;
  total: number;
  processed: number;
  successCount: number;
  failedCount: number;
  errors: Array<{ id: string | number; message: string }>;
  createdAt: Date;
}

// Activity labels mapping - for in-progress state (continuous tense)
const ACTIVITY_LABELS: Record<BulkActivityType, string> = {
  add_tag: 'Adding tags to candidates',
  reject_candidate: 'Rejecting candidates',
  move_to_stage: 'Moving candidates to a stage',
  send_email: 'Sending emails to candidates',
  add_to_job: 'Adding candidates to a job',
  add_to_project: 'Adding candidates to a project',
  add_to_flow: 'Adding candidates to a flow',
  send_self_schedule: 'Sending self-schedule links',
  invite_hireflix: 'Sending Hireflix assessment',
  export_csv: 'Exporting candidates as CSV',
  ai_scoring: 'AI scoring',
  import_csv: 'Importing candidates',
  reactivate_application: 'Reactivating candidates',
  kombo_document_upload: 'Adding documents to HRIS',
  ai_coordinator_dispatch: 'Scheduling calls for candidates',
  background_verification: 'Reviewing candidates with fraud detector',
};

// Activity labels for completed state (past tense)
const ACTIVITY_LABELS_COMPLETED: Record<BulkActivityType, string> = {
  add_tag: 'Added tags to candidates',
  reject_candidate: 'Rejected candidates',
  move_to_stage: 'Moved candidates to stage',
  send_email: 'Sent emails to candidates',
  add_to_job: 'Added candidates to job',
  add_to_project: 'Added candidates to project',
  add_to_flow: 'Added candidates to flow',
  send_self_schedule: 'Sent self-schedule links',
  invite_hireflix: 'Sent Hireflix assessment',
  export_csv: 'Exported candidates as CSV',
  ai_scoring: 'AI scoring completed',
  import_csv: 'Imported candidates',
  reactivate_application: 'Reactivated candidates',
  kombo_document_upload: 'Added documents to HRIS',
  ai_coordinator_dispatch: 'Scheduled calls for candidates',
  background_verification: 'Reviewed candidates with fraud detector',
};

const plural = (n: number) => (n > 1 ? UI_CANDIDATES : UI_CANDIDATE);

// Helper to get label based on status and count
const getActivityLabel = (
  activityType: BulkActivityType,
  status: string,
  total?: number,
  failedCount?: number,
  meta?: { is_scheduled?: boolean }
): string => {
  const isFailed = status === STATUS_FAILED;
  const isCompleted = status === STATUS_COMPLETED;
  const isFinished = isCompleted || isFailed;

  // Labels with count for different activity types
  if (total && total > 0) {
    // Failed status - show "Failed to..." messages
    if (isFailed) {
      const count = failedCount || total;
      switch (activityType) {
        case 'export_csv':
          return `${LABEL_FAILED_TO_EXPORT}${count} ${plural(count)}`;
        case 'import_csv':
          return `${LABEL_FAILED_TO_IMPORT}${count} ${plural(count)}`;
        case 'add_to_job':
        case 'add_to_flow':
        case 'add_to_project':
          return `${LABEL_FAILED_TO_ADD}${count} ${plural(count)}`;
        case 'send_email':
          return `${LABEL_FAILED_TO_EMAIL}${count} ${plural(count)}`;
        case 'move_to_stage':
          return `${LABEL_FAILED_TO_MOVE}${count} ${plural(count)}`;
        case 'reject_candidate':
          return `${LABEL_FAILED_TO_REJECT}${count} ${plural(count)}`;
        case 'add_tag':
          return `${LABEL_FAILED_TO_ADD_TAGS}${count} ${plural(count)}`;
        case 'send_self_schedule':
        case 'invite_hireflix':
          return `${LABEL_FAILED_TO_SEND}${count} ${plural(count)}`;
        case 'reactivate_application':
          return `${LABEL_FAILED_TO_REACTIVATE}${count} ${plural(count)}`;
        case 'ai_scoring':
          return `${LABEL_FAILED_TO_SCORE}${count} ${plural(count)}`;
        case 'ai_coordinator_dispatch':
          return `${LABEL_FAILED_TO_SCHEDULE_CALLS}${count} ${plural(count)}`;
        case 'background_verification':
          return ACTIVITY_LABELS_COMPLETED[activityType];
        default:
          return `${LABEL_FAILED_PREFIX}${ACTIVITY_LABELS_COMPLETED[activityType] || activityType}`;
      }
    }

    // Completed status - show success messages
    if (isCompleted) {
      switch (activityType) {
        case 'export_csv':
          return `${LABEL_EXPORTED}${total} ${plural(total)}`;
        case 'import_csv':
          return `${LABEL_IMPORTED}${total} ${plural(total)}`;
        case 'add_to_job':
        case 'add_to_flow':
        case 'add_to_project':
          return `${LABEL_ADDED}${total} ${plural(total)}`;
        case 'send_email':
          if (meta?.is_scheduled) {
            return `${LABEL_EMAIL_SCHEDULED}${total} ${plural(total)}`;
          }
          return `${LABEL_EMAILED}${total} ${plural(total)}`;
        case 'move_to_stage':
          return `${LABEL_MOVED}${total} ${plural(total)}`;
        case 'reject_candidate':
          return `${LABEL_REJECTED}${total} ${plural(total)}`;
        case 'add_tag':
          return `${LABEL_ADDED_TAGS}${total} ${plural(total)}`;
        case 'send_self_schedule':
        case 'invite_hireflix':
          return `${LABEL_SENT}${total} ${plural(total)}`;
        case 'reactivate_application':
          return `${LABEL_REACTIVATED}${total} ${plural(total)}`;
        case 'ai_scoring':
          return `${LABEL_SCORED}${total} ${plural(total)}`;
        case 'ai_coordinator_dispatch':
          return `${LABEL_SCHEDULED_CALLS} ${total} ${plural(total)}`;
        case 'background_verification':
          return `Reviewed ${total} ${plural(total)} with fraud detector`;
        default:
          return ACTIVITY_LABELS_COMPLETED[activityType] || activityType;
      }
    }

    // In-progress with count
    switch (activityType) {
      case 'export_csv':
        return `${LABEL_EXPORTING}${total} ${plural(total)}`;
      case 'import_csv':
        return `${LABEL_IMPORTING}${total} ${plural(total)}`;
      case 'add_to_job':
      case 'add_to_flow':
      case 'add_to_project':
        return `${LABEL_ADDING}${total} ${plural(total)}`;
      case 'send_email':
        if (meta?.is_scheduled) {
          return `${LABEL_SCHEDULING_EMAIL}${total} ${plural(total)}`;
        }
        return `${LABEL_EMAILING}${total} ${plural(total)}`;
      case 'move_to_stage':
        return `${LABEL_MOVING}${total} ${plural(total)}`;
      case 'reject_candidate':
        return `${LABEL_REJECTING}${total} ${plural(total)}`;
      case 'add_tag':
        return `${LABEL_ADDING_TAGS}${total} ${plural(total)}`;
      case 'send_self_schedule':
      case 'invite_hireflix':
        return `${LABEL_SENDING}${total} ${plural(total)}`;
      case 'reactivate_application':
        return `${LABEL_REACTIVATING}${total} ${plural(total)}`;
      case 'ai_scoring':
        return `${LABEL_SCORING}${total} ${plural(total)}`;
      case 'ai_coordinator_dispatch':
        return `${LABEL_SCHEDULING_CALLS} ${total} ${plural(total)}`;
      case 'background_verification':
        return `Reviewing ${total} ${plural(total)} with fraud detector`;
      default:
        return ACTIVITY_LABELS[activityType] || activityType;
    }
  }

  // Without count, use base labels
  return isFinished
    ? ACTIVITY_LABELS_COMPLETED[activityType] || activityType
    : ACTIVITY_LABELS[activityType] || activityType;
};

interface BulkOperationsContextType {
  /** Current configuration for bulk operations */
  config: BulkOperationQueueConfig | null;
  /** Current view mode */
  viewMode: BulkOperationViewMode;
  /** Total count of operations */
  totalOperations: number;
  /** Overall progress percentage */
  progress: number;
  /** Whether there's a single operation */
  isSingleOperation: boolean;
  /** Title to display */
  title: string;
  /** Whether there are any active operations */
  hasActiveOperations: boolean;
  /** Whether loading */
  isLoading: boolean;
  /** Minimize to sidebar */
  minimizeToSidebar: () => void;
  /** Expand from sidebar */
  expandFromSidebar: () => void;
  /** Hide completely */
  hideQueue: () => void;
  /** Start a new bulk operation - returns operation ID */
  startOperation: (_params: {
    activityType: BulkActivityType;
    title?: string;
    total: number;
  }) => string;
  /** Update operation progress */
  updateOperation: (
    _operationId: string,
    _update: {
      processed?: number;
      successCount?: number;
      failedCount?: number;
      status?: OperationStatus;
      progress?: number;
    }
  ) => void;
  /** Complete operation with results */
  completeOperation: (
    _operationId: string,
    _result: {
      status: 'completed' | 'failed';
      successCount?: number;
      failedCount?: number;
      errors?: Array<{ id: string | number; message: string }>;
      ats_job?: {
        id: number;
        name: string;
        url?: string;
      };
      target?: {
        type: string;
        id: number;
        name: string;
        url?: string;
      };
    }
  ) => void;
  /** Remove operation from queue */
  removeOperation: (_operationId: string) => void;
  /** Clear all completed operations */
  clearCompleted: () => void;
  /** Trigger bulk activities fetch - call after action API response */
  triggerBulkActivitiesFetch: () => void;
  /** Mark activities as viewed (e.g. when user opens Recent activities) */
  markActivitiesAsViewed: () => Promise<void>;
  /** True for a short period after marking as viewed (close/minimize); used to hide NavActivityTracker until refetch completes */
  activitiesJustMarkedAsViewed: boolean;
  /** Whether table bulk actions bar is currently visible (rows selected) */
  isBulkActionsVisible: boolean;
  /** Set whether table bulk actions bar is visible - used to hide GlobalBulkOperationQueue */
  setBulkActionsVisible: (_visible: boolean) => void;
}

const BulkOperationsContext = createContext<BulkOperationsContextType | null>(
  null
);

interface BulkOperationsProviderProps {
  children: ReactNode;
}

export const BulkOperationsProvider = ({
  children,
}: BulkOperationsProviderProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] =
    useState<BulkOperationViewMode>(VIEW_MODE_HIDDEN);
  const [isManuallyHidden, setIsManuallyHidden] = useState(false);
  // Track when table bulk actions bar is visible (rows selected in table)
  const [isBulkActionsVisible, setIsBulkActionsVisible] = useState(false);
  const [activitiesJustMarkedAsViewed, setActivitiesJustMarkedAsViewed] =
    useState(false);
  const [localOperations, setLocalOperations] = useState<LocalBulkOperation[]>(
    []
  );

  // Check if user is logged in - don't poll if not authenticated
  const user = useSelector(selectUser);
  const isAuthenticated = Boolean(user?.id);

  // CSV Import status from Redux (uses different API: /user_import_statuses/)
  const csvImportData = useSelector(selectContactImport);
  const csvImportById = useSelector(selectContactImportById);
  const csvImportPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Ref to store last valid config to prevent flickering during data transitions
  const lastValidConfigRef = useRef<BulkOperationQueueConfig | null>(null);
  // Retain last known activity count when queue is minimized so NavActivityTracker stays visible
  // after operations complete (API may return 0). Cleared when user opens Recent activities.
  const [lastKnownCountWhenMinimized, setLastKnownCountWhenMinimized] =
    useState(0);

  // Poll bulk activities API with skip_viewed=true
  // Auto-fetch on mount when authenticated to check for any in-progress activities
  const {
    data: apiData,
    hasActiveActivities: apiHasActive,
    isPolling,
    triggerFetch,
  } = useBulkActivitiesPolling({
    autoFetch: isAuthenticated, // Auto-fetch on mount when authenticated (with skip_viewed=true)
    pollInterval: 5000, // 5 seconds
    onAllComplete: (activities) => {
      // Dispatch a custom event so pages can listen and refresh their data
      window.dispatchEvent(
        new CustomEvent(EVENT_BULK_OPERATIONS_COMPLETE, {
          detail: { activities },
        })
      );
    },
  });

  // Fetch pending CSV import on mount - only if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(
        fetchPendingImport(undefined) as unknown as Parameters<
          typeof dispatch
        >[0]
      );
    }
  }, [dispatch, isAuthenticated]);

  // Poll for CSV import progress when there's an active import
  useEffect(() => {
    // Don't poll if not authenticated
    if (!isAuthenticated) {
      return;
    }

    const importId = csvImportData?.data?.id;
    const importStatus =
      csvImportById?.data?.import_status || csvImportData?.data?.import_status;

    // Clear existing polling
    if (csvImportPollingRef.current) {
      clearInterval(csvImportPollingRef.current);
      csvImportPollingRef.current = null;
    }

    // Start polling if we have an active import
    if (
      importId &&
      importStatus !== IMPORT_STATUS_DONE &&
      importStatus !== IMPORT_STATUS_FAILED
    ) {
      // Poll every 3 seconds for CSV import status
      csvImportPollingRef.current = setInterval(() => {
        dispatch(
          fetchImportById({ importId }) as unknown as Parameters<
            typeof dispatch
          >[0]
        );
        // Also poll bulk activities to keep them in sync
        triggerFetch();
      }, 3000);

      // Also expand the queue
      if (!isManuallyHidden) {
        setViewMode(VIEW_MODE_EXPANDED);
      }

      // Trigger bulk activities fetch immediately
      triggerFetch();
    }

    return () => {
      if (csvImportPollingRef.current) {
        clearInterval(csvImportPollingRef.current);
      }
    };
  }, [
    csvImportData?.data?.id,
    csvImportById?.data?.import_status,
    csvImportData?.data?.import_status,
    dispatch,
    isManuallyHidden,
    isAuthenticated,
    triggerFetch,
  ]);

  // Note: Don't auto-trigger bulk activities fetch here.
  // Callers should use triggerBulkActivitiesFetch() after their action API response.

  // Normalize activity type helper - maps to exact API keys
  const normalizeActivityType = useCallback((activityType: string): string => {
    // These are the exact keys from the API - no normalization needed for these
    const supportedKeys = [
      'add_tag',
      'reject_candidate',
      'move_to_stage',
      'send_email',
      'add_to_job',
      'add_to_project',
      'add_to_flow',
      'send_self_schedule',
      'invite_hireflix',
      'export_csv',
      'ai_scoring',
      'import_csv',
      'reactivate_application',
      'kombo_document_upload',
      'ai_coordinator_dispatch',
    ];

    // If it's already a supported key, return as-is
    if (supportedKeys.includes(activityType)) {
      return activityType;
    }

    // Legacy mappings for backwards compatibility with local operations
    const legacyMap: Record<string, string> = {
      csv_import: 'import_csv' as const,
      reject: 'reject_candidate' as const,
      ai_score: 'ai_scoring' as const,
      move_stage: 'move_to_stage' as const,
      add_tags: 'add_tag' as const,
      reactivate: 'reactivate_application' as const,
      self_schedule: 'send_self_schedule' as const,
    };

    return legacyMap[activityType] || activityType;
  }, []);

  // Cleanup local operations that are now superseded by API data
  // This prevents memory leaks from accumulating local operations and duplicate items
  // Cleanup when API has ANY activities for that type (in_progress, completed, or failed)
  // (meaning the server has received and processed the operation)
  useEffect(() => {
    if (!apiData?.activities) return;

    // Get activity types that have ANY activities in API data
    // We should cleanup local ops when API has data for that type, regardless of status
    const apiActivityTypes = new Set<string>();
    apiData.activities.forEach((activity) => {
      apiActivityTypes.add(normalizeActivityType(activity.activity));
    });

    // Remove local operations that have been superseded by API data
    if (apiActivityTypes.size > 0) {
      setLocalOperations((prev) =>
        prev.filter(
          (op) => !apiActivityTypes.has(normalizeActivityType(op.activityType))
        )
      );
    }
  }, [apiData?.activities, normalizeActivityType]);

  // Generate unique operation ID
  const generateOperationId = useCallback(() => {
    return `${OP_ID_PREFIX}${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Start a new bulk operation
  const startOperation = useCallback(
    (params: {
      activityType: BulkActivityType;
      title?: string;
      total: number;
    }) => {
      const operationId = generateOperationId();
      const newOperation: LocalBulkOperation = {
        id: operationId,
        activityType: params.activityType,
        title: params.title || ACTIVITY_LABELS[params.activityType],
        status: STATUS_IN_PROGRESS as OperationStatus,
        progress: 0,
        total: params.total,
        processed: 0,
        successCount: 0,
        failedCount: 0,
        errors: [],
        createdAt: new Date(),
      };

      // NOTE: Don't clear cache here - it triggers an immediate refetch before the action API call
      // The cache will be properly refreshed when triggerBulkActivitiesFetch is called after the action completes

      setLocalOperations((prev) => [...prev, newOperation]);
      setViewMode(VIEW_MODE_EXPANDED);
      setIsManuallyHidden(false);

      // Notify listeners (e.g. JobCandidates table) to clear selection so bulk action bar hides immediately
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(EVENT_BULK_ACTION_STARTED));
      }

      return operationId;
    },
    [generateOperationId]
  );

  // Update operation progress
  const updateOperation = useCallback(
    (
      operationId: string,
      update: {
        processed?: number;
        successCount?: number;
        failedCount?: number;
        status?: OperationStatus;
        progress?: number;
      }
    ) => {
      setLocalOperations((prev) =>
        prev.map((op) => {
          if (op.id !== operationId) return op;

          const processed = update.processed ?? op.processed;
          const progress =
            update.progress ??
            (op.total > 0 ? Math.round((processed / op.total) * 100) : 0);

          return {
            ...op,
            ...update,
            processed,
            progress,
          };
        })
      );
    },
    []
  );

  // Complete operation with results
  const completeOperation = useCallback(
    (
      operationId: string,
      result: {
        status: 'completed' | 'failed';
        successCount?: number;
        failedCount?: number;
        errors?: Array<{ id: string | number; message: string }>;
      }
    ) => {
      setLocalOperations((prev) =>
        prev.map((op) => {
          if (op.id !== operationId) return op;

          return {
            ...op,
            status: result.status,
            progress: 100,
            processed: op.total,
            successCount: result.successCount ?? op.successCount,
            failedCount: result.failedCount ?? op.failedCount,
            errors: result.errors ?? op.errors,
          };
        })
      );

      // Auto-cleanup completed local operations after 3 seconds
      // Since completed local ops are not shown in the UI (API data takes over),
      // we just need a short delay for any in-flight renders
      setTimeout(() => {
        setLocalOperations((prev) =>
          prev.filter((op) => op.id !== operationId)
        );
      }, 3000);
    },
    []
  );

  // Remove operation from queue
  const removeOperation = useCallback((operationId: string) => {
    setLocalOperations((prev) => prev.filter((op) => op.id !== operationId));
  }, []);

  // Clear all completed operations
  const clearCompleted = useCallback(() => {
    setLocalOperations((prev) =>
      prev.filter(
        (op) => op.status === STATUS_IN_PROGRESS || op.status === STATUS_PENDING
      )
    );
  }, []);

  // Check if there's an active CSV import (must have an id to be valid)
  const hasCsvImport = useMemo(() => {
    const importData = csvImportById?.data || csvImportData?.data;
    // Check if import data exists AND has an id (not empty object)
    return (
      importData &&
      importData.id &&
      importData.import_status !== IMPORT_STATUS_DONE &&
      importData.import_status !== IMPORT_STATUS_FAILED
    );
  }, [csvImportById?.data, csvImportData?.data]);

  // Check if there are any active operations (local, from API, or CSV imports)
  const hasActiveOperations =
    localOperations.length > 0 || apiHasActive || hasCsvImport;

  // Calculate overall progress from both local operations and API activities
  const progress = useMemo(() => {
    const allProgressValues: number[] = [];

    // Add local operation progress
    localOperations.forEach((op) => {
      allProgressValues.push(op.progress);
    });

    // Add API activity progress (avoid duplicates)
    if (apiData?.activities) {
      apiData.activities.forEach((activity) => {
        // Only add if not already tracked locally
        const isTrackedLocally = localOperations.some(
          (op) => op.id === String(activity.id)
        );
        if (!isTrackedLocally) {
          allProgressValues.push(activity.progress?.percentage || 0);
        }
      });
    }

    if (allProgressValues.length === 0) return 0;

    // Calculate average progress
    const totalProgress = allProgressValues.reduce((acc, val) => acc + val, 0);
    return Math.round(totalProgress / allProgressValues.length);
  }, [localOperations, apiData?.activities]);

  // Transform local operations and API activities to config format
  const config: BulkOperationQueueConfig | null = useMemo(() => {
    // Check if we have any local operations (including completed/failed)
    const hasLocalOps = localOperations.length > 0;
    const hasApiActivities =
      (apiData?.activities && apiData.activities.length > 0) ||
      (apiData?.failed && apiData.failed.length > 0);

    if (!hasLocalOps && !hasApiActivities && !hasCsvImport) return null;

    // Group local operations by activity type
    const groupedOperations: Record<string, BulkOperationItem[]> = {};

    // Get activity types that have ANY activities in API data
    // We skip local operations when API has ANY data for that type (including completed)
    // because the API data represents the actual server-side status
    const apiActivityTypes = new Set<string>();
    if (apiData?.activities) {
      apiData.activities.forEach((activity) => {
        apiActivityTypes.add(normalizeActivityType(activity.activity));
      });
    }

    // Add local operations to groups
    // Rules:
    // 1. Show local operations ONLY if API doesn't have ANY data for that activity type
    //    (local operations provide immediate feedback before API receives the new operation)
    // 2. Once API has data for an activity type (any status), skip local operations of that type
    //    (API data takes priority to show accurate server-side status)
    localOperations.forEach((op) => {
      const key = normalizeActivityType(op.activityType);

      // Skip local operations if API has ANY activity for this type
      // This means the server has responded with data for this operation
      if (apiActivityTypes.has(key)) {
        return;
      }

      if (!groupedOperations[key]) {
        groupedOperations[key] = [];
      }
      groupedOperations[key].push({
        id: op.id,
        name: op.title,
        status: op.status,
        progress: op.progress,
        errorType: op.errors.length > 0 ? op.errors[0].message : undefined,
        successCount: op.successCount,
        failedCount: op.failedCount,
      });
    });

    // Check if we have an active CSV import from Redux (uses different API: /user_import_statuses/)
    // We add this FIRST to prevent duplicates with bulk_activities
    const csvImportFromRedux = csvImportById?.data || csvImportData?.data;
    // Only consider Redux CSV import "active" if it's not done AND not failed
    // This prevents skipping API activities when Redux has a completed/failed import
    const hasActiveCsvImportFromRedux =
      csvImportFromRedux &&
      csvImportFromRedux.id &&
      csvImportFromRedux.import_status !== IMPORT_STATUS_DONE &&
      csvImportFromRedux.import_status !== IMPORT_STATUS_FAILED;

    // Add API activities (avoid duplicates by checking if already tracked locally)
    // Helper function to add activity to grouped operations
    const addActivityToGroup = (activity: BulkActivityItem) => {
      const key = normalizeActivityType(activity.activity);

      if (!groupedOperations[key]) {
        groupedOperations[key] = [];
      }
      // Only add if not already tracked locally
      const alreadyTracked = groupedOperations[key].some(
        (item) => item.id === String(activity.id)
      );
      if (!alreadyTracked) {
        // Map API status to UI status - ensure it's a valid OperationStatus
        let uiStatus: OperationStatus = activity.status;
        if (
          uiStatus !== STATUS_PENDING &&
          uiStatus !== STATUS_IN_PROGRESS &&
          uiStatus !== STATUS_COMPLETED &&
          uiStatus !== STATUS_FAILED
        ) {
          uiStatus = STATUS_IN_PROGRESS as OperationStatus; // fallback
        }

        const total = activity.progress?.total || 0;
        const successCount = activity.progress?.success || 0;
        const failedCount = activity.progress?.failed || 0;

        // Treat as failed ONLY if status is completed but there are actual failures
        // Don't mark as failed just because success is 0 - items could be skipped (not failures)
        // Exception: background_verification is always shown as completed (green tick)
        if (
          uiStatus === STATUS_COMPLETED &&
          failedCount > 0 &&
          successCount === 0 &&
          key !== 'background_verification'
        ) {
          uiStatus = STATUS_FAILED as OperationStatus;
        }

        // Get appropriate name based on activity type, status, and count
        // Use getActivityLabel for status-aware labels with count (pass failedCount for failed messages)
        let itemName = getActivityLabel(
          key as BulkActivityType,
          uiStatus,
          total,
          failedCount,
          activity.meta
        );

        // Override with specific names for certain activity types
        if (key === 'add_to_project') {
          const projectName =
            activity.project?.name || activity.project_name || UI_PROJECT;
          if (uiStatus === STATUS_FAILED) {
            itemName = `${LABEL_FAILED_TO_ADD}${failedCount || total} ${(failedCount || total) !== 1 ? UI_CANDIDATES : UI_CANDIDATE} to ${projectName}`;
          } else if (uiStatus === STATUS_COMPLETED) {
            itemName = `${LABEL_ADDED}${total} ${total !== 1 ? UI_CANDIDATES : UI_CANDIDATE} to ${projectName}`;
          } else {
            itemName = `${LABEL_ADDING}${total} ${total !== 1 ? UI_CANDIDATES : UI_CANDIDATE} to ${projectName}`;
          }
        } else if (key === 'add_to_flow') {
          const flowName = activity.flow?.name || activity.flow_name || UI_FLOW;
          if (uiStatus === STATUS_FAILED) {
            itemName = `${LABEL_FAILED_TO_ADD}${failedCount || total} ${(failedCount || total) !== 1 ? UI_CANDIDATES : UI_CANDIDATE} to ${flowName}`;
          } else if (uiStatus === STATUS_COMPLETED) {
            itemName = `${LABEL_ADDED}${total} ${total !== 1 ? UI_CANDIDATES : UI_CANDIDATE} to ${flowName}`;
          } else {
            itemName = `${LABEL_ADDING}${total} ${total !== 1 ? UI_CANDIDATES : UI_CANDIDATE} to ${flowName}`;
          }
        } else if (key === 'ai_scoring') {
          itemName =
            activity.ats_job?.name ||
            activity.job_name ||
            getActivityLabel(
              key as BulkActivityType,
              uiStatus,
              total,
              failedCount,
              activity.meta
            );
        } else if (key === 'import_csv') {
          // For CSV import, show file name with status-appropriate verb (file_name can be on activity or in meta)
          const fileName =
            activity.file_name ||
            activity.meta?.file_name ||
            activity.ats_job?.name ||
            UI_CSV;
          if (uiStatus === STATUS_FAILED) {
            itemName = `Failed to import ${fileName}`;
          } else if (uiStatus === STATUS_COMPLETED) {
            itemName = `${LABEL_IMPORTED}${fileName}`;
          } else {
            itemName = `${LABEL_IMPORTING}${fileName}`;
          }
        } else if (key === 'ai_coordinator_dispatch') {
          if (uiStatus === STATUS_FAILED) {
            itemName = `${LABEL_FAILED_TO_SCHEDULE_CALLS}${failedCount || total} ${plural(failedCount || total)}`;
          } else if (uiStatus === STATUS_COMPLETED) {
            itemName = `${LABEL_SCHEDULED_CALLS} ${total} ${plural(total)}`;
          } else {
            itemName = `${LABEL_SCHEDULING_CALLS} ${total} ${plural(total)}`;
          }
        } else if (key === 'background_verification') {
          const jobName = activity.target?.name;
          if (jobName) {
            itemName = `${itemName} for ${jobName}`;
          }
        }

        groupedOperations[key].push({
          id: String(activity.id),
          name: itemName,
          status: uiStatus,
          progress: activity.progress?.percentage || 0,
          errorType: activity.error_type,
          successCount: activity.progress?.success,
          failedCount: activity.progress?.failed,
        });
      }
    };

    // Add activities from main list
    if (apiData?.activities) {
      apiData.activities.forEach(addActivityToGroup);
    }

    // Also add failed activities if any
    if (apiData?.failed) {
      apiData.failed.forEach(addActivityToGroup);
    }

    // Add Redux CSV import only when API has no import_csv activities (avoid duplicate count)
    // When API already returns import_csv activities, use those only so we show the correct count (e.g. 3 not 4)
    const apiHasImportCsvActivities =
      apiData?.activities?.some(
        (a) => normalizeActivityType(a.activity) === 'import_csv'
      ) ?? false;
    if (hasActiveCsvImportFromRedux && !apiHasImportCsvActivities) {
      const csvImport = csvImportFromRedux;
      const key = 'import_csv';
      if (!groupedOperations[key]) {
        groupedOperations[key] = [];
      }

      // Check if this import ID is already tracked
      const alreadyTracked = groupedOperations[key].some(
        (item) => item.id === `${CSV_ID_PREFIX}${csvImport.id}`
      );

      if (!alreadyTracked) {
        // Calculate progress from CSV import data
        // Handle both field name formats: total_rows/processed_rows and total/successful
        const total = csvImport.total_rows || csvImport.total || 0;
        const processed = csvImport.processed_rows || csvImport.successful || 0;
        const csvProgress =
          total > 0 ? Math.round((processed / total) * 100) : 0;

        // Map status
        let csvStatus: OperationStatus = STATUS_IN_PROGRESS as OperationStatus;
        if (csvImport.import_status === IMPORT_STATUS_DONE) {
          csvStatus = STATUS_COMPLETED as OperationStatus;
        } else if (csvImport.import_status === IMPORT_STATUS_FAILED) {
          csvStatus = STATUS_FAILED as OperationStatus;
        }

        groupedOperations[key].push({
          id: `${CSV_ID_PREFIX}${csvImport.id}`,
          name: csvImport.file_name || UI_CSV_IMPORT,
          status: csvStatus,
          progress: csvProgress,
          successCount:
            csvImport.success_count || csvImport.successful || processed,
          failedCount: csvImport.failed_count || csvImport.failed || 0,
        });
      }
    }

    // Transform to groups, then sort: in-progress first (groups with any in_progress/pending at top, then items within each group)
    const STATUS_PRIORITY = {
      [STATUS_IN_PROGRESS]: 0,
      [STATUS_PENDING]: 1,
      [STATUS_COMPLETED]: 2,
      [STATUS_FAILED]: 3,
    };
    const groups: BulkOperationGroup[] = Object.entries(groupedOperations)
      .map(([activityType, items]) => {
        const sortedItems = [...items].sort(
          (a, b) =>
            (STATUS_PRIORITY[a?.status] ?? 2) -
            (STATUS_PRIORITY[b?.status] ?? 2)
        );
        return {
          id: activityType,
          title:
            ACTIVITY_LABELS[activityType as BulkActivityType] || activityType,
          items: sortedItems,
        };
      })
      .sort((a, b) => {
        const aHasActive = a?.items?.some(
          (i) => i.status === STATUS_IN_PROGRESS || i?.status === STATUS_PENDING
        );
        const bHasActive = b?.items?.some(
          (i) =>
            i?.status === STATUS_IN_PROGRESS || i?.status === STATUS_PENDING
        );
        if (aHasActive && !bHasActive) return -1;
        if (!aHasActive && bHasActive) return 1;
        return 0;
      });

    const newConfig = {
      groups,
      progress,
      showExpandButton: true,
      showCloseButton: true,
    };

    // Stabilization: If the new config has groups, update the ref
    // If it's empty but we had groups before, keep showing the previous config briefly
    // to prevent flickering during data transitions
    if (groups.length > 0) {
      lastValidConfigRef.current = newConfig;
      return newConfig;
    }

    // If we have a previous valid config with groups, keep showing it
    // This prevents the brief "empty" state during refetch
    if (
      lastValidConfigRef.current &&
      lastValidConfigRef.current.groups.length > 0
    ) {
      return lastValidConfigRef.current;
    }

    return newConfig;
  }, [
    localOperations,
    apiData,
    progress,
    csvImportById?.data,
    csvImportData?.data,
    hasCsvImport,
    normalizeActivityType,
  ]);

  // Total operations count: only in_progress and completed from API (and local/CSV)
  const totalOperations = useMemo(() => {
    let count = localOperations.length;

    // Add API activities that are in_progress or completed only (not pending/failed)
    let apiHasImportCsv = false;
    if (apiData?.activities) {
      apiData.activities.forEach((activity) => {
        const isInProgressOrCompleted =
          activity.status === STATUS_IN_PROGRESS ||
          activity.status === STATUS_COMPLETED;
        const isTrackedLocally = localOperations.some(
          (op) => op.id === String(activity.id)
        );
        if (isInProgressOrCompleted && !isTrackedLocally) {
          count += 1;
          if (normalizeActivityType(activity.activity) === 'import_csv') {
            apiHasImportCsv = true;
          }
        }
      });
    }

    // Add CSV import if active, but only when API doesn't already have this import (avoid double-count)
    if (hasCsvImport && !apiHasImportCsv) {
      count += 1;
    }

    return count;
  }, [
    localOperations,
    apiData?.activities,
    hasCsvImport,
    normalizeActivityType,
  ]);

  // Check if there are any failed API activities
  const hasFailedActivities = useMemo(() => {
    return apiData?.failed && apiData.failed.length > 0;
  }, [apiData?.failed]);

  // Check if there are any completed API activities (to keep queue visible after completion)
  const hasCompletedActivities = useMemo(() => {
    return (
      apiData?.activities?.some(
        (activity) => activity.status === STATUS_COMPLETED
      ) || false
    );
  }, [apiData?.activities]);

  // Keep showing after completion until user views Recent activities (markActivitiesAsViewed)
  const showAfterCompletion = useMemo(() => {
    return (
      (hasCompletedActivities || hasFailedActivities) &&
      !apiHasActive &&
      !hasCsvImport
    );
  }, [hasCompletedActivities, hasFailedActivities, apiHasActive, hasCsvImport]);

  // Effective view mode - show when there are active or failed operations
  const effectiveViewMode = useMemo(() => {
    // If manually hidden by user, stay hidden
    if (isManuallyHidden) {
      return VIEW_MODE_HIDDEN;
    }

    // Auto-expand when activities are detected (including failed and recently completed)
    const hasActivities =
      hasActiveOperations ||
      apiHasActive ||
      hasFailedActivities ||
      hasCompletedActivities ||
      showAfterCompletion;

    // Keep minimized when user closed to sidebar and we have retained count (API may have returned 0 after completion)
    if (
      viewMode === VIEW_MODE_MINIMIZED &&
      !hasActivities &&
      lastKnownCountWhenMinimized > 0
    ) {
      return VIEW_MODE_MINIMIZED;
    }

    if (hasActivities) {
      // If currently hidden but we have activities, auto-expand
      if (viewMode === VIEW_MODE_HIDDEN) {
        return VIEW_MODE_EXPANDED;
      }
      return viewMode;
    }

    // No activities, hide
    return VIEW_MODE_HIDDEN;
  }, [
    hasActiveOperations,
    apiHasActive,
    hasFailedActivities,
    hasCompletedActivities,
    showAfterCompletion,
    viewMode,
    isManuallyHidden,
    lastKnownCountWhenMinimized,
  ]);

  // Keep state updated when we have activities (so we can retain count after API returns 0 on completion)
  useEffect(() => {
    if (totalOperations > 0) {
      setLastKnownCountWhenMinimized(totalOperations);
    }
  }, [totalOperations]);

  // When minimized/hidden, keep showing NavActivityTracker with last known count until user opens Recent activities
  const totalOperationsDisplay =
    (effectiveViewMode === VIEW_MODE_MINIMIZED ||
      effectiveViewMode === VIEW_MODE_HIDDEN) &&
    totalOperations === 0 &&
    lastKnownCountWhenMinimized > 0
      ? lastKnownCountWhenMinimized
      : totalOperations;

  const isSingleOperation = totalOperationsDisplay === 1;

  const title = useMemo(() => {
    if (!config) return '';
    if (isSingleOperation) {
      // Check local operations first
      if (localOperations[0]) {
        return localOperations[0].title;
      }
      // Then check API activities
      if (apiData?.activities?.[0]) {
        const activity = apiData.activities[0];
        const key = activity.activity as BulkActivityType;
        const total = activity.progress?.total || 0;
        const isCompleted = activity.status === STATUS_COMPLETED;
        // Get appropriate title based on activity type
        if (key === 'add_to_project') {
          return `${LABEL_ADDING}${total} ${total !== 1 ? UI_CANDIDATES : UI_CANDIDATE} to ${activity.project?.name || activity.project_name || UI_PROJECT}`;
        } else if (key === 'add_to_flow') {
          return `${LABEL_ADDING}${total} ${total !== 1 ? UI_CANDIDATES : UI_CANDIDATE} to ${activity.flow?.name || activity.flow_name || UI_FLOW}`;
        } else if (key === 'ai_scoring') {
          return (
            activity.ats_job?.name || activity.job_name || ACTIVITY_LABELS[key]
          );
        } else if (key === 'send_email' && activity.meta?.is_scheduled) {
          return isCompleted
            ? `${LABEL_EMAIL_SCHEDULED}${total} ${total !== 1 ? UI_CANDIDATES : UI_CANDIDATE}`
            : `${LABEL_SCHEDULING_EMAIL}${total} ${total !== 1 ? UI_CANDIDATES : UI_CANDIDATE}`;
        }
        return (
          activity.ats_job?.name ||
          activity.job_name ||
          activity.file_name ||
          ACTIVITY_LABELS[key] ||
          activity.activity
        );
      }
      // Check CSV import (must have id to be valid)
      const csvImport = csvImportById?.data || csvImportData?.data;
      if (csvImport && csvImport.id) {
        return csvImport.file_name || UI_IMPORTING_CSV;
      }
      return UI_PROCESSING;
    }
    return `${totalOperations}${UI_ACTIVITIES_IN_PROGRESS_SUFFIX}`;
  }, [
    config,
    isSingleOperation,
    localOperations,
    apiData?.activities,
    csvImportById?.data,
    csvImportData?.data,
    totalOperations,
  ]);

  // Get the highest completed/failed activity ID for marking as viewed
  const getHighestCompletedOrFailedActivityId = useCallback(() => {
    const allActivities = [
      ...(apiData?.activities || []),
      ...(apiData?.failed || []),
    ];

    // Filter only completed or failed activities
    const completedOrFailed = allActivities.filter(
      (activity) =>
        activity.status === STATUS_COMPLETED ||
        activity.status === STATUS_FAILED
    );

    if (completedOrFailed.length === 0) {
      return null;
    }

    // Find the highest ID among completed/failed activities
    const highestId = Math.max(
      ...completedOrFailed.map((activity) => activity.id)
    );
    return highestId;
  }, [apiData?.activities, apiData?.failed]);

  // Mark activities as viewed by sending highest completed/failed ID
  const markActivitiesAsViewed = useCallback(async () => {
    const highestId = getHighestCompletedOrFailedActivityId();
    if (highestId) {
      setLastKnownCountWhenMinimized(0); // allow tracker to hide after refetch
      setActivitiesJustMarkedAsViewed(true);
      try {
        await BULK_OPERATION_QUEUE_API_SERVICES.markActivitiesAsViewed(
          highestId
        );
        await queryClient.invalidateQueries({
          queryKey: BULK_ACTIVITIES_QUERY_KEYS.listWithSkipViewed(),
        });
      } catch (error) {
        setActivitiesJustMarkedAsViewed(false);
        console.error(LOG_MARK_ACTIVITIES_VIEWED_FAILED, error);
      }
      // Hide NavActivityTracker until refetch completes; clear flag after 2s
      setTimeout(() => setActivitiesJustMarkedAsViewed(false), 2000);
    }
  }, [getHighestCompletedOrFailedActivityId, queryClient]);

  const minimizeToSidebar = useCallback(() => {
    setViewMode(VIEW_MODE_MINIMIZED);
    setIsManuallyHidden(false);
    // Do not mark as viewed here - keep Activity Tracker in left nav until user clicks it and views Recent activities
  }, []);

  const expandFromSidebar = useCallback(() => {
    setViewMode('expanded');
    setIsManuallyHidden(false);
  }, []);

  const hideQueue = useCallback(() => {
    setViewMode(VIEW_MODE_HIDDEN);
    setIsManuallyHidden(true);
    // Do not mark as viewed here - keep Activity Tracker in left nav until user views Recent activities
  }, []);

  // Callback to set bulk actions visibility from Table component
  const setBulkActionsVisible = useCallback((visible: boolean) => {
    setIsBulkActionsVisible(visible);
  }, []);

  const value: BulkOperationsContextType = {
    config,
    viewMode: effectiveViewMode,
    totalOperations: totalOperationsDisplay,
    progress,
    isSingleOperation,
    title,
    hasActiveOperations,
    isLoading: isPolling,
    minimizeToSidebar,
    expandFromSidebar,
    hideQueue,
    startOperation,
    updateOperation,
    completeOperation,
    removeOperation,
    clearCompleted,
    triggerBulkActivitiesFetch: triggerFetch,
    markActivitiesAsViewed,
    activitiesJustMarkedAsViewed,
    isBulkActionsVisible,
    setBulkActionsVisible,
  };

  return (
    <BulkOperationsContext.Provider value={value}>
      {children}
    </BulkOperationsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useBulkOperations = () => {
  const context = useContext(BulkOperationsContext);
  if (!context) {
    throw new Error(
      'useBulkOperations must be used within a BulkOperationsProvider'
    );
  }
  return context;
};

// Optional hook that returns null if not in provider
// eslint-disable-next-line react-refresh/only-export-components
export const useBulkOperationsOptional = () => {
  return useContext(BulkOperationsContext);
};
